import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { colors, shadows } from '../../../styles/theme/colors';
import { Logo } from '../../common/Logo/Logo';
import PlaneIcon from '../../../../assets/plane (2).svg';
import BoxIcon from '../../../../assets/box (2).svg';
import translations from '../../../translations/en.json';

interface RoleSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onRoleSelect: (role: 'traveller' | 'sender') => Promise<void>;
  loading?: boolean;
}

const RoleSelectionDialog: React.FC<RoleSelectionDialogProps> = ({
  open,
  onClose,
  onRoleSelect,
  loading = false,
}) => {
  const [selectedRole, setSelectedRole] = useState<'traveller' | 'sender' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRoleClick = (role: 'traveller' | 'sender') => {
    if (!isSubmitting) {
      setSelectedRole(role);
    }
  };

  const handleContinue = async () => {
    if (!selectedRole || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onRoleSelect(selectedRole);
    } catch { /* ignore */ } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Get button color based on selected role
  const getButtonColor = () => {
    if (!selectedRole) return colors.neutral.gray300;
    return selectedRole === 'traveller' ? colors.traveller.primary : colors.sender.primary;
  };

  const getButtonHoverColor = () => {
    if (!selectedRole || isSubmitting) return colors.neutral.gray300;
    return selectedRole === 'traveller' ? '#7A1448' : '#5A0042';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '90%', sm: '512px' },
          maxWidth: '512px',
          height: 'auto',
          borderRadius: '12px',
          boxShadow: shadows.xl,
          position: 'relative',
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      }}
    >
      {/* Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: { xs: '24px 16px 0', sm: '32px 24px 0' },
            gap: '16px',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Logo */}
          <Logo
            variant="dark"
            sx={{
              width: '121.6px',
              height: '32px',
            }}
          />

          {/* Title and Description */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '27px',
                textAlign: 'center',
                color: '#181D27',
              }}
            >
              {translations.roleSelectionDialog.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '21px',
                textAlign: 'center',
                color: '#535862',
              }}
            >
              {translations.roleSelectionDialog.description}
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              position: 'absolute',
              right: { xs: '8px', sm: '16px' },
              top: { xs: '8px', sm: '16px' },
              padding: '8px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon sx={{ width: 24, height: 24, color: '#A4A7AE' }} />
          </IconButton>
        </Box>

        {/* Padding Bottom */}
        <Box sx={{ height: '20px' }} />

        {/* Content - Role Selection Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            padding: { xs: '0 16px', sm: '0 24px' },
            gap: '12px',
            width: '100%',
          }}
        >
          {/* Traveller Card */}
          <Box
            onClick={() => handleRoleClick('traveller')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: { xs: '12px 8px', sm: '16px 12px' },
              gap: '8px',
              flex: 1,
              backgroundColor: colors.neutral.white,
              boxShadow: selectedRole === 'traveller'
                ? `0px 0px 0px 2px ${colors.traveller.primary}`
                : '0px 2px 14px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
              '&:hover': {
                boxShadow: selectedRole === 'traveller'
                  ? `0px 0px 0px 2px ${colors.traveller.primary}`
                  : '0px 4px 16px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {/* Radio Button */}
            <Box
              sx={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: selectedRole === 'traveller'
                  ? `6px solid ${colors.traveller.primary}`
                  : '2px solid #D1D5DB',
                backgroundColor: selectedRole === 'traveller'
                  ? colors.traveller.primary
                  : 'transparent',
                transition: 'all 0.2s ease',
              }}
            />

            {/* Icon */}
            <Box
              component="img"
              src={PlaneIcon}
              alt="Traveller"
              sx={{
                width: { xs: '48px', sm: '56.92px' },
                height: { xs: '42px', sm: '50px' },
                objectFit: 'contain',
              }}
            />

            {/* Title */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: { xs: '13px', sm: '15px' },
                lineHeight: '27px',
                color: '#181D27',
                textAlign: 'center',
              }}
            >
              {translations.roleSelectionDialog.traveler.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: { xs: '12px', sm: '14px' },
                lineHeight: '21px',
                color: '#535862',
                textAlign: 'center',
              }}
            >
              {translations.roleSelectionDialog.traveler.description}
            </Typography>
          </Box>

          {/* Sender Card */}
          <Box
            onClick={() => handleRoleClick('sender')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: { xs: '12px 8px', sm: '16px 12px' },
              gap: '8px',
              flex: 1,
              backgroundColor: colors.neutral.white,
              boxShadow: selectedRole === 'sender'
                ? `0px 0px 0px 2px ${colors.sender.primary}`
                : '0px 2px 14px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
              '&:hover': {
                boxShadow: selectedRole === 'sender'
                  ? `0px 0px 0px 2px ${colors.sender.primary}`
                  : '0px 4px 16px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {/* Radio Button */}
            <Box
              sx={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: selectedRole === 'sender'
                  ? `6px solid ${colors.sender.primary}`
                  : '2px solid #D1D5DB',
                backgroundColor: selectedRole === 'sender'
                  ? colors.sender.primary
                  : 'transparent',
                transition: 'all 0.2s ease',
              }}
            />

            {/* Icon */}
            <Box
              component="img"
              src={BoxIcon}
              alt="Sender"
              sx={{
                width: { xs: '48px', sm: '56.92px' },
                height: { xs: '42px', sm: '50px' },
                objectFit: 'contain',
              }}
            />

            {/* Title */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: { xs: '13px', sm: '15px' },
                lineHeight: '27px',
                color: '#181D27',
                textAlign: 'center',
              }}
            >
              {translations.roleSelectionDialog.sender.title}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: { xs: '12px', sm: '14px' },
                lineHeight: '21px',
                color: '#535862',
                textAlign: 'center',
              }}
            >
              {translations.roleSelectionDialog.sender.description}
            </Typography>
          </Box>
        </Box>

        {/* Continue Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: { xs: '20px 16px', sm: '24px 24px' },
            width: '100%',
          }}
        >
          <Box
            onClick={handleContinue}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px 24px',
              width: '100%',
              height: '48px',
              background: getButtonColor(),
              borderRadius: '8px',
              cursor: selectedRole && !isSubmitting ? 'pointer' : 'not-allowed',
              opacity: selectedRole && !isSubmitting ? 1 : 0.6,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: selectedRole && !isSubmitting
                  ? getButtonHoverColor()
                  : colors.neutral.gray300,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: colors.neutral.white }} />
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: colors.neutral.white,
                }}
              >
                {translations.roleSelectionDialog.continueButton}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RoleSelectionDialog;
